// Tier-A verification: the deterministic, total half of FR-7 that S4 left a seam for. Given the
// loaded map and the outcome of resolving each cited `file:line` against the tree (the only I/O,
// done by the injectable gatherer), this module computes every mechanical map-integrity finding —
// no model tokens, reproducible over identical inputs. It is pure: `collectMapEvidence` lists the
// pointers the gatherer must resolve, and `collectVerificationFindings` turns the map + those
// resolutions into candidate findings plus any check it could not run (recorded as blocked, never
// a silent pass — INV-5).
//
// The categories, mirroring the codebase-health detector split:
//   SM-REMOVE   — a mapped surface whose every cited file is gone (its backing code was removed).
//   SM-EVIDENCE — any other cited `file:line` that no longer resolves (stale line, or a broken
//                 citation on a guard/transition). Complements SM-REMOVE so no broken pointer
//                 escapes both — the "every cited file:line must resolve" total.
//   SM-XREF     — a transition target or a guard reference that names an id the map never defines.
//   SM-ORPHAN   — a surface unreachable from any entry (only when the map models a graph).
//   SM-DEADEND  — a navigational surface with no way out and no terminal marker.
//   SM-GUARDLESS— a backstage (sensitive) surface with no guard.

import { PATHS } from '@/core/constants/paths.js';
import type {
  AppMap,
  Area,
  Evidence,
  EvidenceRef,
  Surface,
  SurfaceKind,
} from '@/core/types/site-map.js';
import type { SiteMapBlockedCheck, SiteMapFinding } from '@/core/types/site-map-run.js';

/** How a single cited `file:line` resolves against the working tree (the gatherer's verdict). */
export type EvidenceStatus = 'resolved' | 'file-missing' | 'line-missing';

/** The gatherer's resolution of one evidence pointer (the only I/O Tier-A depends on). */
export interface EvidenceResolution {
  file: string;
  line?: number;
  status: EvidenceStatus;
}

/** A finding before the caller content-addresses it into an `SM-<hash8>` id. */
export type Candidate = Omit<SiteMapFinding, 'id'>;

/** Navigational surface kinds that MUST lead somewhere (else they are a dead end). The remaining
 * kinds — actions, api/cli/job endpoints, terminals, hand-offs, decision-pauses — are leaf-like by
 * nature and never flagged. */
const NAVIGATIONAL_KINDS: ReadonlySet<SurfaceKind> = new Set<SurfaceKind>([
  'page',
  'screen',
  'modal',
  'router',
  'prompt-entry',
  'llm-workflow',
  'step',
  'subflow',
]);

/** Normalize a single-or-list evidence ref into a flat array. */
export function normalizeEvidence(ref: EvidenceRef | undefined): Evidence[] {
  if (ref === undefined) return [];
  return Array.isArray(ref) ? ref : [ref];
}

/** A resolved status lookup for one evidence pointer (an unlisted pointer reads as resolved). */
export type StatusOf = (pointer: Evidence) => EvidenceStatus;

/**
 * Index the gatherer's resolutions by pointer so callers can ask the status of any evidence
 * pointer. A pointer the gatherer did not resolve reads as `resolved` — the cautious default that
 * raises nothing rather than a phantom defect. Shared by the evidence checks and the trust-tier
 * proof (`./trust.ts`) so both read resolutions the same way.
 */
export function buildStatusOf(resolutions: EvidenceResolution[]): StatusOf {
  const status = new Map<string, EvidenceStatus>();
  for (const resolution of resolutions) {
    status.set(evidenceKey({ file: resolution.file, line: resolution.line }), resolution.status);
  }
  return (pointer) => status.get(evidenceKey(pointer)) ?? 'resolved';
}

/** Normalize a single-or-list id reference into a flat array. */
function asList(ref: string | string[] | undefined): string[] {
  if (ref === undefined) return [];
  return Array.isArray(ref) ? ref : [ref];
}

/** Stable lookup key for an evidence pointer (line-sensitive). */
function evidenceKey(pointer: Evidence): string {
  return `${pointer.file} ${pointer.line ?? ''}`;
}

/**
 * Human `file:line` (line dropped when absent), for finding proof and resolutions. Exported as the
 * one canonical anchor form so the creation-questions derivation (`./creation-answers.ts`) attaches
 * the same `file:line` strings the map cites, instead of re-implementing the format.
 */
export function locate(pointer: Evidence): string {
  return pointer.line === undefined ? pointer.file : `${pointer.file}:${pointer.line}`;
}

/**
 * Every distinct evidence pointer the map cites — across surfaces, their inline transitions, and
 * guards — deduped. `run.ts` hands this to the gatherer, which resolves each against the tree.
 */
export function collectMapEvidence(map: AppMap | null): Evidence[] {
  if (map === null) return [];
  const byKey = new Map<string, Evidence>();
  const add = (ref: EvidenceRef | undefined): void => {
    for (const pointer of normalizeEvidence(ref)) {
      const key = evidenceKey(pointer);
      if (!byKey.has(key)) byKey.set(key, pointer);
    }
  };
  for (const surface of map.surfaces) {
    add(surface.evidence);
    for (const transition of surface.transitions ?? []) add(transition.evidence);
  }
  for (const guard of map.guards ?? []) add(guard.evidence);
  return [...byKey.values()];
}

/** A backstage surface (directly, or by inheriting a backstage area) is treated as sensitive. */
function isBackstage(surface: Surface, areaById: Map<string, Area>): boolean {
  if (surface.visibility !== undefined) return surface.visibility === 'backstage';
  const area = surface.area === undefined ? undefined : areaById.get(surface.area);
  return area?.visibility === 'backstage';
}

/** A surface is guarded when it, or its area, carries at least one guard. */
function isGuarded(surface: Surface, areaById: Map<string, Area>): boolean {
  if (asList(surface.guard).length > 0) return true;
  const area = surface.area === undefined ? undefined : areaById.get(surface.area);
  return asList(area?.guard).length > 0;
}

/** A navigational surface is terminal-marked when it carries a truthy `ends` state. (A `terminal`
 * node kind is never navigational, so it never reaches this check — the exempt-kinds filter drops
 * it first.) */
function hasTerminalMarker(surface: Surface): boolean {
  const ends = surface.ends;
  if (ends === undefined) return false;
  return Boolean(ends.success) || Boolean(ends.failure) || Boolean(ends.abandoned);
}

/**
 * SM-REMOVE + SM-EVIDENCE: reconcile every cited pointer against the resolution map. A surface
 * whose citations are all `file-missing` reads as removed (its code is gone); any other broken
 * pointer — a stale line, or a broken citation on a guard or transition — is an evidence defect.
 * The two are mutually exclusive per surface, so no pointer is double-counted and none escapes.
 */
function detectEvidenceFindings(map: AppMap, resolutions: EvidenceResolution[]): Candidate[] {
  const statusOf = buildStatusOf(resolutions);

  const candidates: Candidate[] = [];

  // Surfaces: a full file-loss is a removal; anything else broken is an evidence defect.
  for (const surface of map.surfaces) {
    const pointers = normalizeEvidence(surface.evidence);
    if (pointers.length === 0) continue;
    const broken = pointers.filter((pointer) => statusOf(pointer) !== 'resolved');
    if (broken.length === 0) continue;
    const files = [...new Set(broken.map((pointer) => pointer.file))].sort();
    if (broken.length === pointers.length && broken.every((p) => statusOf(p) === 'file-missing')) {
      candidates.push({
        title: `Removed surface: ${surface.label}`,
        description:
          `The ${surface.kind} "${surface.label}" is on the map, but every file it cites is gone ` +
          'from the tree, so the map describes code that no longer exists.',
        category: 'SM-REMOVE',
        severity: 'high',
        tier: 'deterministic',
        confidence: 1,
        evidence: broken.map(
          (pointer) =>
            `app-map.yaml surface "${surface.id}" cites ${pointer.file} — file not found`,
        ),
        resolution:
          `Remove surface "${surface.id}" from ${PATHS.SITE_MAP_CANONICAL_APP_MAP}, or point ` +
          'its evidence at the code that replaced it.',
        affected_surfaces: [surface.id],
        affected_files: files,
        baseline_status: 'unknown',
        status: 'open',
      });
      continue;
    }
    candidates.push(
      evidenceDefect(
        `surface "${surface.id}"`,
        `surface "${surface.label}"`,
        surface.id,
        broken,
        (pointer) =>
          `app-map.yaml surface "${surface.id}" cites ${locate(pointer)} — ${reason(statusOf(pointer))}`,
      ),
    );
  }

  // Guards: a broken citation is always an evidence defect (a guard is not a surface).
  for (const guard of map.guards ?? []) {
    const broken = normalizeEvidence(guard.evidence).filter((p) => statusOf(p) !== 'resolved');
    if (broken.length === 0) continue;
    candidates.push(
      evidenceDefect(
        `guard "${guard.id}"`,
        `guard "${guard.label}"`,
        undefined,
        broken,
        (pointer) =>
          `app-map.yaml guard "${guard.id}" cites ${locate(pointer)} — ${reason(statusOf(pointer))}`,
      ),
    );
  }

  // Transitions live inside a surface; a broken citation is attributed to that surface.
  for (const surface of map.surfaces) {
    for (const transition of surface.transitions ?? []) {
      const broken = normalizeEvidence(transition.evidence).filter(
        (p) => statusOf(p) !== 'resolved',
      );
      if (broken.length === 0) continue;
      candidates.push(
        evidenceDefect(
          `transition ${surface.id} → ${transition.to}`,
          `the transition from "${surface.id}" to "${transition.to}"`,
          surface.id,
          broken,
          (pointer) =>
            `app-map.yaml transition ${surface.id} → ${transition.to} cites ${locate(pointer)} — ${reason(statusOf(pointer))}`,
        ),
      );
    }
  }

  return candidates;
}

/** English reason for a non-resolving pointer. */
function reason(status: EvidenceStatus): string {
  return status === 'file-missing' ? 'file not found' : 'line out of range';
}

/** Build a single SM-EVIDENCE finding for one owner's broken pointers. */
function evidenceDefect(
  ownerId: string,
  ownerPhrase: string,
  affectedSurface: string | undefined,
  broken: Evidence[],
  proof: (pointer: Evidence) => string,
): Candidate {
  const files = [...new Set(broken.map((pointer) => pointer.file))].sort();
  return {
    title: `Stale evidence on ${ownerId}`,
    description:
      `The evidence for ${ownerPhrase} no longer resolves in the tree, so the map cites code that ` +
      'has moved or been deleted and can no longer be trusted as proof.',
    category: 'SM-EVIDENCE',
    severity: 'medium',
    tier: 'deterministic',
    confidence: 1,
    evidence: broken.map(proof),
    resolution:
      `Re-anchor the evidence for ${ownerPhrase} to its current file:line, or remove the stale ` +
      `pointer, in ${PATHS.SITE_MAP_CANONICAL_APP_MAP}.`,
    affected_surfaces: affectedSurface === undefined ? [] : [affectedSurface],
    affected_files: files,
    baseline_status: 'unknown',
    status: 'open',
  };
}

/**
 * SM-XREF: every transition target must name an existing surface, and every guard reference —
 * on a surface, a transition, an area, or an actor — must name an existing guard. One finding per
 * dangling reference, so each names exactly what is missing.
 */
function detectDanglingReferences(map: AppMap): Candidate[] {
  const surfaceIds = new Set(map.surfaces.map((surface) => surface.id));
  const guardIds = new Set((map.guards ?? []).map((guard) => guard.id));
  const candidates: Candidate[] = [];

  const guardRef = (
    refs: Array<string | string[] | undefined>,
    ownerId: string,
    ownerPhrase: string,
    affectedSurface: string | undefined,
  ): void => {
    for (const id of refs.flatMap(asList)) {
      if (guardIds.has(id)) continue;
      candidates.push({
        title: `Undefined guard "${id}" referenced by ${ownerId}`,
        description:
          `${ownerPhrase} requires guard "${id}", but no guard with that id is defined, so the ` +
          'map claims a protection that does not exist.',
        category: 'SM-XREF',
        severity: 'high',
        tier: 'deterministic',
        confidence: 1,
        evidence: [`app-map.yaml — ${ownerId} references guard "${id}" (not in guards)`],
        resolution:
          `Define a guard with id "${id}" in ${PATHS.SITE_MAP_CANONICAL_APP_MAP}, or correct ` +
          `the reference on ${ownerId}.`,
        affected_surfaces: affectedSurface === undefined ? [] : [affectedSurface],
        affected_files: [],
        baseline_status: 'unknown',
        status: 'open',
      });
    }
  };

  for (const surface of map.surfaces) {
    guardRef(
      [surface.guard, surface.guard_not],
      `surface "${surface.id}"`,
      `Surface "${surface.label}"`,
      surface.id,
    );
    for (const transition of surface.transitions ?? []) {
      if (!surfaceIds.has(transition.to)) {
        candidates.push({
          title: `Dangling transition: ${surface.id} → ${transition.to}`,
          description:
            `Surface "${surface.id}" has a "${transition.trigger}" transition to "${transition.to}", ` +
            'but no surface with that id exists, so the path leads nowhere.',
          category: 'SM-XREF',
          severity: 'high',
          tier: 'deterministic',
          confidence: 1,
          evidence: [
            `app-map.yaml — transition ${surface.id} → ${transition.to} (no such surface)`,
          ],
          resolution:
            `Add a surface with id "${transition.to}" to ${PATHS.SITE_MAP_CANONICAL_APP_MAP}, ` +
            `or repoint the "${transition.trigger}" transition on "${surface.id}" at an existing surface.`,
          affected_surfaces: [surface.id],
          affected_files: [],
          baseline_status: 'unknown',
          status: 'open',
        });
      }
      guardRef(
        [transition.guard, transition.guard_not],
        `transition ${surface.id} → ${transition.to}`,
        `The transition from "${surface.id}" to "${transition.to}"`,
        surface.id,
      );
    }
  }
  for (const area of map.areas ?? []) {
    guardRef([area.guard], `area "${area.id}"`, `Area "${area.label}"`, undefined);
  }
  for (const actor of map.actors ?? []) {
    guardRef([actor.satisfies], `actor "${actor.id}"`, `Actor "${actor.label}"`, undefined);
  }

  return candidates;
}

/** BFS the surface graph from the entry roots, following only transitions to existing surfaces. */
function reachableFrom(roots: string[], surfaces: Surface[]): Set<string> {
  const surfaceIds = new Set(surfaces.map((surface) => surface.id));
  const outgoing = new Map<string, string[]>();
  for (const surface of surfaces) {
    outgoing.set(
      surface.id,
      (surface.transitions ?? [])
        .map((transition) => transition.to)
        .filter((to) => surfaceIds.has(to)),
    );
  }
  const reached = new Set<string>();
  const queue = [...roots];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (reached.has(id)) continue;
    reached.add(id);
    // Every id enqueued is a known surface (roots and filtered targets alike), so its adjacency
    // list is always present.
    for (const next of outgoing.get(id)!) queue.push(next);
  }
  return reached;
}

/**
 * The graph invariants (SM-ORPHAN / SM-DEADEND) and the guard-less-sensitive invariant
 * (SM-GUARDLESS). Reachability and dead-ends only apply once the map models a graph (has at least
 * one transition); a graph with no entry root cannot be rooted and is recorded as a blocked check.
 */
function detectGraphInvariants(map: AppMap): {
  candidates: Candidate[];
  blocked: SiteMapBlockedCheck[];
} {
  const candidates: Candidate[] = [];
  const blocked: SiteMapBlockedCheck[] = [];
  const areaById = new Map<string, Area>((map.areas ?? []).map((area) => [area.id, area]));
  const hasGraph = map.surfaces.some((surface) => (surface.transitions?.length ?? 0) > 0);

  if (hasGraph) {
    const roots = map.surfaces.filter((surface) => surface.entry !== undefined).map((s) => s.id);
    if (roots.length === 0) {
      blocked.push({
        check: 'reachability',
        reason: 'the map models transitions but marks no entry surface, so the graph has no root',
        install_hint:
          `Mark at least one surface with an \`entry:\` block in ${PATHS.SITE_MAP_CANONICAL_APP_MAP} ` +
          'so reachability can be computed.',
      });
    } else {
      const reached = reachableFrom(roots, map.surfaces);
      for (const surface of map.surfaces) {
        if (reached.has(surface.id)) continue;
        candidates.push({
          title: `Orphan surface: ${surface.label}`,
          description:
            `The ${surface.kind} "${surface.label}" cannot be reached from any entry surface, so no ` +
            'user path leads to it — it is either dead code or an unmapped transition.',
          category: 'SM-ORPHAN',
          severity: 'medium',
          tier: 'deterministic',
          confidence: 1,
          evidence: [`app-map.yaml — surface "${surface.id}" is unreachable from every entry`],
          resolution:
            `Add a transition that reaches "${surface.id}", give it its own \`entry:\`, or remove it ` +
            `from ${PATHS.SITE_MAP_CANONICAL_APP_MAP}.`,
          affected_surfaces: [surface.id],
          affected_files: [],
          baseline_status: 'unknown',
          status: 'open',
        });
      }
    }

    for (const surface of map.surfaces) {
      if (!NAVIGATIONAL_KINDS.has(surface.kind)) continue;
      if (hasTerminalMarker(surface)) continue;
      if ((surface.transitions?.length ?? 0) > 0) continue;
      candidates.push({
        title: `Dead-end surface: ${surface.label}`,
        description:
          `The ${surface.kind} "${surface.label}" has no outgoing transition and is not marked ` +
          'terminal, so a user who reaches it has no modelled way to leave.',
        category: 'SM-DEADEND',
        severity: 'low',
        tier: 'deterministic',
        confidence: 1,
        evidence: [
          `app-map.yaml — surface "${surface.id}" has no outgoing transition and no \`ends\``,
        ],
        resolution:
          `Add an outgoing transition to "${surface.id}", or mark it terminal with an \`ends:\` block, ` +
          `in ${PATHS.SITE_MAP_CANONICAL_APP_MAP}.`,
        affected_surfaces: [surface.id],
        affected_files: [],
        baseline_status: 'unknown',
        status: 'open',
      });
    }
  } else {
    // A map with no transitions models no navigation, so reachability and dead-end checks have
    // nothing to run — record that skip as a blocked check (INV-5) instead of a silent pass.
    blocked.push({
      check: 'reachability',
      reason:
        'the map records no navigation between screens, so reachability and dead ends cannot be checked',
      install_hint:
        `Record transitions between surfaces in ${PATHS.SITE_MAP_CANONICAL_APP_MAP} so ` +
        'reachability and dead ends can be checked.',
    });
  }

  for (const surface of map.surfaces) {
    if (!isBackstage(surface, areaById)) continue;
    if (isGuarded(surface, areaById)) continue;
    candidates.push({
      title: `Unguarded sensitive surface: ${surface.label}`,
      description:
        `The backstage ${surface.kind} "${surface.label}" carries no guard, so the map shows a ` +
        'sensitive surface with nothing gating access to it.',
      category: 'SM-GUARDLESS',
      severity: 'high',
      tier: 'deterministic',
      confidence: 1,
      evidence: [`app-map.yaml — backstage surface "${surface.id}" has no guard`],
      resolution:
        `Add a \`guard:\` to surface "${surface.id}" (or to its area) in ` +
        `${PATHS.SITE_MAP_CANONICAL_APP_MAP}, or reclassify it as frontstage if it is not sensitive.`,
      affected_surfaces: [surface.id],
      affected_files: [],
      baseline_status: 'unknown',
      status: 'open',
    });
  }

  return { candidates, blocked };
}

/**
 * Run every Tier-A check over the map and the gatherer's evidence resolutions. Returns the
 * candidate findings (ids assigned by the caller) and any check that could not run (blocked).
 * With no map there is nothing to verify, but that gap is recorded as a `map-present` blocked
 * check rather than passed over in silence (INV-5): a run over a project with no authored map
 * must never read as clean.
 */
export function collectVerificationFindings(
  map: AppMap | null,
  resolutions: EvidenceResolution[],
): { candidates: Candidate[]; blockedChecks: SiteMapBlockedCheck[] } {
  if (map === null) {
    return {
      candidates: [],
      blockedChecks: [
        {
          check: 'map-present',
          reason:
            `no site map has been authored at ${PATHS.SITE_MAP_CANONICAL_APP_MAP} yet, so there ` +
            'is nothing to check the code against',
          install_hint:
            `Author the site map at ${PATHS.SITE_MAP_CANONICAL_APP_MAP} (one surface entry per ` +
            'page, screen, or command), then run the site-map workflow to verify it.',
        },
      ],
    };
  }
  const graph = detectGraphInvariants(map);
  return {
    candidates: [
      ...detectEvidenceFindings(map, resolutions),
      ...detectDanglingReferences(map),
      ...graph.candidates,
    ],
    blockedChecks: graph.blocked,
  };
}
